import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLang } from "../i18n";
import { fetchBuildId } from "../lib/version";
import { Download, RefreshCw, X, Share, Plus, CheckCircle2 } from "lucide-react";

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isMobile = () =>
  /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent) ||
  (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.platform)); // iPadOS
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export default function InstallApp() {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [waiting, setWaiting] = useState(null);
  const [newBuild, setNewBuild] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const baselineBuild = useRef(null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setDeferred(null); setInstalled(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Build-version checker: compares the deployed asset-manifest hash to the one
  // loaded at startup. A newer build => prompt the user to update.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const id = await fetchBuildId();
      if (cancelled || !id) return;
      if (baselineBuild.current == null) {
        baselineBuild.current = id;
      } else if (id !== baselineBuild.current) {
        setNewBuild(true);
      }
    };
    check();
    const interval = setInterval(check, 60000);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      reg.update().catch(() => {});
      if (reg.waiting) setWaiting(reg.waiting);
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) setWaiting(reg.waiting || nw);
        });
      });
    });
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        navigator.serviceWorker.getRegistration().then((reg) => reg && reg.update().catch(() => {}));
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const updateAvailable = newBuild || !!waiting;

  const doUpdate = () => {
    if (waiting) waiting.postMessage({ type: "SKIP_WAITING" });
    // Reload to fetch the freshly deployed HTML + hashed bundles (network-first).
    setTimeout(() => window.location.reload(), 150);
  };

  const doInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setDeferred(null);
    } else {
      setShowHelp(true);
    }
  };

  // Update available → priority action (any platform)
  if (updateAvailable) {
    return (
      <button
        data-testid="update-app-btn"
        onClick={doUpdate}
        className="flex items-center gap-2 p-2 sm:px-3 text-sm label-caps text-primary border border-primary/50 rounded-sm hover:bg-primary/10 transition-colors animate-pulse"
        title={t("update_app")}
      >
        <RefreshCw size={16} /> <span className="hidden sm:inline">{t("update_app")}</span>
      </button>
    );
  }

  // Already installed and no update → nothing to show
  if (installed) return null;

  // Install prompt only relevant on mobile devices (Android / iOS)
  if (!isMobile()) return null;

  return (
    <>
      <button
        data-testid="install-app-btn"
        onClick={doInstall}
        className="flex items-center gap-2 p-2 sm:px-3 text-sm label-caps text-zinc-300 border border-zinc-700 rounded-sm hover:border-primary hover:text-primary transition-colors"
        title={t("install_app")}
      >
        <Download size={16} /> <span className="hidden sm:inline">{t("install_app")}</span>
      </button>

      {showHelp && createPortal(
        <div
          data-testid="install-help-modal"
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading text-xl text-white">{t("install_title")}</h2>
              <button data-testid="install-help-close" onClick={() => setShowHelp(false)} className="text-zinc-500 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {isIOS() ? (
              <ol className="space-y-4 text-sm text-zinc-300">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">1.</span>
                  <span className="flex items-center gap-2 flex-wrap">{t("install_ios_1")} <Share size={16} className="inline text-primary" /></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">2.</span>
                  <span className="flex items-center gap-2 flex-wrap">{t("install_ios_2")} <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-800 rounded text-xs">{t("install_ios_add")} <Plus size={12} /></span></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">3.</span>
                  <span>{t("install_ios_3")}</span>
                </li>
              </ol>
            ) : (
              <div className="space-y-3 text-sm text-zinc-300">
                <p>{t("install_other_1")}</p>
                <p className="text-zinc-400">{t("install_other_2")}</p>
              </div>
            )}

            <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
              <CheckCircle2 size={14} className="text-primary" /> {t("install_benefit")}
            </div>

            <button
              data-testid="install-help-ok"
              onClick={() => setShowHelp(false)}
              className="mt-5 w-full py-2.5 bg-primary text-primary-foreground label-caps text-sm rounded-sm font-semibold hover:bg-yellow-400 transition-colors"
            >
              {t("close")}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
